import { ViewType, ViewObject } from '../base';

import { dataService } from '../../data';
import { Family } from '../objects/family';
import { FamilyPeriod } from '../objects/familyperiod';
import { FamilyProduct } from '../objects/familyproduct';

/****************************/
/* CLASS VIEW               */
/****************************/

export class _FamilyView extends ViewObject<Family> {
    
    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get family(): Family{
        return this.object;
    }
             
    constructor(object: Family, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION       */
    /**********************************/

    private _validperiods: Array <FamilyPeriod> | null = null;
    private _validproducts: Array <FamilyProduct> | null = null;

    private _productslength = 0;
    
    private RefreshView(targets: string[]){
        if (targets.includes('FAMILY')){
            this.DoRefreshView();
        }

        if (targets.includes('FAMILYPERIOD')){
            this._validperiods = null;
            this.DoRefreshView();
        }

        if (targets.includes('FAMILYPRODUCT')){
            this._validproducts = null;
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void{
        const weakref = new WeakRef(this);

        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];

        this._subscriptions.push(
            this.family.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){  // do not keep strong references in subscription
                    self.RefreshView(targets);
                }
            })
        )    
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/
    
    get periods(): Array <FamilyPeriod> {
        if (this._validperiods == null){
            const _periods: Array <FamilyPeriod> = []; 

            for(const _period of this.family.periods){
                if (_period.IsValid){
                    _periods.push(_period);
                }
            }

            this._validperiods = _periods;
        }

        return this._validperiods;
    }

    get products(): Array <FamilyProduct> {
        if (this._validproducts == null){
            const _products: Array <FamilyProduct> = []; 

            this._productslength = 0;
            for(const _product of this.family.products){
                if (_product.IsValid){
                    _products.push(_product);
                    this._productslength++;
                }
            }

            _products.sort((a, b) => {
                return (a.sort || 0) - (b.sort || 0);
            })

            this._validproducts = _products;
        }

        return this._validproducts;
    }

    // calculated at the same time as the products list
    get ProductsLength(): number {
        if (this.products.length === 0){
            return 0;   // this forces the products calculation
        }

        return this._productslength;
    }

    /**********************************/
    /* ACTIVE STATE                   */
    /**********************************/

    /**
     * Whether this family is selectable in the cart: valid + status AC +
     * (allday or any period active now). Same pattern as Offer/Discount/Extra.
     */
    get IsActive(): boolean {
        if (!this.family.IsValid || this.family.status !== 'AC'){
            return false;
        }

        if (this.family.allday){
            return true;
        }

        const _now = new Date();
        for (const period of this.periods){
            if (period.IsActiveAt(_now)){
                return true;
            }
        }

        return false;
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type FamilyView = ViewType <_FamilyView, Family>;

export function FamilyViewProxy(object: Family, data: dataService): FamilyView {
    return new _FamilyView(object, data).Proxy as FamilyView;
}
